const cleanSpaces = (text) =>
  String(text || "")
    .replace(/\s+([,.!?;:])/g, "$1")
    .replace(/\s+/g, " ")
    .trim();

const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const replacePhrase = (text, source, replacement) => {
  const pattern = new RegExp(`(^|[^\\p{L}])(${escapeRegExp(source)})(?=$|[^\\p{L}])`, "giu");
  return text.replace(pattern, (_, prefix) => `${prefix}${replacement}`);
};

const applyPhraseMap = (input, phraseMap) => {
  let output = String(input || "");
  phraseMap.forEach(([source, replacement]) => {
    output = replacePhrase(output, source, replacement);
  });
  return output;
};

const normalizePhraseKey = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[.!?]+$/g, "")
    .replace(/\s+/g, " ")
    .trim();

const replaceWords = (input, dictionary) =>
  String(input || "").replace(/[\p{L}]+/giu, (word) => dictionary[word.toLowerCase()] || word);

const OTHER_VALUE = "Muu, mikä?";

const EMPTY_OR_TECHNICAL_VALUES = new Set(["", OTHER_VALUE.toLowerCase(), "valitse..."]);

export const USER_CONTENT_FIELD_KEYS = [
  "idea", "userIdea", "shortIdea", "topic", "subject", "promptTopic", "description", "scene", "action", "mainAction",
  "setting", "environment", "originalPrompt", "prompt", "goal", "objective", "desiredOutcome", "audience", "context",
  "brandVoice", "tone", "style", "length", "constraints", "notes", "examples", "aihe", "teema", "kohde", "tilanne",
  "aloitus", "tapahtuma", "ymparisto", "ympäristö", "kohderyhma", "kohderyhmä", "tavoite", "savy", "sävy",
  "tyyli", "pituus", "valttavat", "erityishuomiot", "lukitut", "vaatetus", "tarina", "tuote", "palvelu",
  "ongelma", "kayttotilanne", "hyoty", "tausta", "lopputulos", "konteksti"
];

const stringifyFormValue = (input) => {
  if (Array.isArray(input)) return input.filter(Boolean).join(", ");
  return String(input || "").trim();
};

export const hasMeaningfulUserInput = (input) => {
  const value = stringifyFormValue(input).trim();
  return Boolean(value) && !EMPTY_OR_TECHNICAL_VALUES.has(value.toLowerCase());
};

export const getPrimaryUserInput = (form = {}, preferredKeys = [], includeRemainingFields = true) => {
  const uniqueKeys = includeRemainingFields
    ? [...preferredKeys, ...USER_CONTENT_FIELD_KEYS.filter((key) => !preferredKeys.includes(key)), ...Object.keys(form).filter((key) => !key.endsWith("__other") && !preferredKeys.includes(key))]
    : preferredKeys;
  for (const key of uniqueKeys) {
    const current = form[key];
    const other = stringifyFormValue(form[`${key}__other`]);
    const value = Array.isArray(current)
      ? [...current.filter((item) => item && item !== OTHER_VALUE), other].filter(Boolean).join(", ")
      : current === OTHER_VALUE
        ? other
        : stringifyFormValue(current);
    if (hasMeaningfulUserInput(value)) return value;
  }
  return "";
};

export const detectInputLanguage = (input) => {
  const text = String(input || "").trim();
  if (!text) return "unknown";
  if (/[äöå]/i.test(text)) return "fi";
  if (/\b(haluan|luo|tee|nainen|mies|henkilö|kuva|video|metsä|ranta|valo|vaatetus|kasvot|vältä|säilytä|luonnollinen|realistinen|yrittäjä|sisällöntuottaja)\b/i.test(text)) return "fi";
  return "en";
};

const fiToEnPhrases = [
  ["kuvan henkilö katsoo Helsingin tuomiokirkkoa haltioitunut ilme kasvoillaan", "the person from the attached reference image looking at Helsinki Cathedral with an amazed, captivated expression on their face"],
  ["kuvan henkilö katsoo helsingin tuomiokirkkoa haltioitunut ilme kasvoillaan", "the person from the attached reference image looking at Helsinki Cathedral with an amazed, captivated expression on their face"],
  ["kuvan nainen katsoo Helsingin tuomiokirkkoa haltioitunut ilme kasvoillaan", "the woman from the attached reference image looking at Helsinki Cathedral with an amazed, captivated expression on her face"],
  ["kuvan hahmo istuu laiturilla suomalaisessa kesämaisemassa järven rannalla", "the person from the attached reference image sitting on a wooden dock by a Finnish lakeside in a summer landscape"],
  ["kuvan henkilö istuu laiturilla suomalaisessa kesämaisemassa järven rannalla", "the person from the attached reference image sitting on a wooden dock by a Finnish lakeside in a summer landscape"],
  ["kuvan nainen katsoo järvimaisemaa", "the woman from the attached reference image looking at a Finnish lakeside landscape"],
  ["kuvan henkilö katsoo järvimaisemaa", "the person from the attached reference image looking at a Finnish lakeside landscape"],
  ["hahmo istuu laiturilla suomalaisessa kesämaisemassa järven rannalla", "a person sitting on a wooden dock by a Finnish lakeside in a summer landscape"],
  ["henkilö istuu laiturilla suomalaisessa kesämaisemassa järven rannalla", "a person sitting on a wooden dock by a Finnish lakeside in a summer landscape"],
  ["kuvan henkilö", "the person from the attached reference image"],
  ["kuvan hahmo", "the person from the attached reference image"],
  ["katsoo Helsingin tuomiokirkkoa", "looking at Helsinki Cathedral"],
  ["katsoo helsingin tuomiokirkkoa", "looking at Helsinki Cathedral"],
  ["Helsingin tuomiokirkkoa", "Helsinki Cathedral"],
  ["helsingin tuomiokirkkoa", "Helsinki Cathedral"],
  ["Helsingin tuomiokirkko", "Helsinki Cathedral"],
  ["helsingin tuomiokirkko", "Helsinki Cathedral"],
  ["katsoo tunturimaisemaa", "looking out over a Finnish fell landscape"],
  ["katsoo järvimaisemaa", "looking at a Finnish lakeside landscape"],
  ["katsoo metsämaisemaa", "looking at a Finnish forest landscape"],
  ["katsoo merimaisemaa", "looking at the sea view"],
  ["katsoo auringonlaskua", "looking at the sunset"],
  ["katsoo maisemaa", "looking at the landscape"],
  ["ihailee tunturimaisemaa", "admiring a Finnish fell landscape"],
  ["ihailee järvimaisemaa", "admiring a Finnish lakeside landscape"],
  ["ihailee auringonlaskua", "admiring the sunset"],
  ["haltioitunut ilme kasvoillaan", "with an amazed, captivated expression on their face"],
  ["luonnollinen ilme", "with a natural expression"],
  ["kevyt hymy", "with a soft smile"],
  ["haltioitunut ilme", "an amazed, captivated expression"],
  ["haluan tehdä", "I want to create"],
  ["haluan luoda", "I want to create"],
  ["realistisen videon", "a realistic video"],
  ["realistinen video", "a realistic video"],
  ["realistisen kuvan", "a realistic image"],
  ["realistinen kuva", "a realistic image"],
  ["liitetty kuva", "attached image"],
  ["lähtökuva", "starting image"],
  ["referenssikuva", "reference image"],
  ["kuvan valotilanne", "the lighting conditions from the reference image"],
  ["kuvan valo tilanne", "the lighting conditions from the reference image"],
  ["kuvan pitää", "the image should"],
  ["videon pitää", "the video should"],
  ["pitää tuntua", "should feel"],
  ["tuntua aidolta", "feel authentic"],
  ["ei liian tuotetulta mainokselta", "not like an overly produced advertisement"],
  ["ei liian mainosmaiselta", "not overly promotional"],
  ["ei liian kiillotettu", "not overly polished"],
  ["ei liian täydellinen", "not overly perfect"],
  ["ei tekoälymäinen", "not AI-looking"],
  ["tekoälymäinen lopputulos", "an AI-looking result"],
  ["liian kiillotettu tyyli", "an overly polished style"],
  ["muovinen iho", "plastic skin"],
  ["ylisiloteltu ulkonäkö", "over-smoothed appearance"],
  ["epäluonnollinen liike", "unnatural movement"],
  ["kasvojen vääristyminen", "face distortion"],
  ["sormien vääristyminen", "distorted fingers"],
  ["hahmon identiteetin muuttuminen", "changes to the subject's identity"],
  ["korporaatiojargon", "corporate jargon"],
  ["liian mainosmainen kieli", "overly promotional language"],
  ["liika hype", "excessive hype"],
  ["epäuskottavat lupaukset", "unbelievable promises"],
  ["suomalainen nainen", "a Finnish woman"],
  ["suomalaisessa metsässä", "in a Finnish forest"],
  ["suomalaisessa kesämaisemassa", "in a Finnish summer landscape"],
  ["nainen istuu suomalaisessa metsässä kaatuneen puun päällä ja paistaa makkaraa nuotiolla", "a woman sitting on a fallen tree in a Finnish forest and roasting sausages over a campfire"],
  ["nainen istuu suomalaisessa metsässä", "a woman sitting in a Finnish forest"],
  ["suomalainen mies", "a Finnish man"],
  ["suomalainen yrittäjä", "a Finnish entrepreneur"],
  ["suomalainen yrittäjä tekee somevideon tekoälystä", "a Finnish entrepreneur creating a social media video about artificial intelligence"],
  ["yrittäjä tekee somevideon tekoälystä", "an entrepreneur creating a social media video about artificial intelligence"],
  ["somevideo tekoälystä", "a social media video about artificial intelligence"],
  ["somepostaus tekoälyn hyödyntämisestä sisällöntuotannossa", "a social media post about using AI in content creation"],
  ["somepostaus tekoälystä", "a social media post about artificial intelligence"],
  ["tekoälyn hyödyntämisestä sisällöntuotannossa", "using AI in content creation"],
  ["tekoälyn hyödyntäminen sisällöntuotannossa", "using AI in content creation"],
  ["suomalainen sisällöntuottaja", "a Finnish content creator"],
  ["suomalaiset sisällöntuottajat", "Finnish content creators"],
  ["suomalainen sisällöntuottaja kotitoimistossa juo kahvia ja suunnittelee ensi viikon sisältöjä", "a Finnish content creator in a home office, drinking coffee and planning next week's content"],
  ["suomalainen sisällöntuottaja kotitoimistossa juomassa kahvia ja suunnittelemassa ensi viikon sisältöjä", "a Finnish content creator in a home office, drinking coffee and planning next week's content"],
  ["sisällöntuottaja kotitoimistossa juo kahvia ja suunnittelee ensi viikon sisältöjä", "a content creator in a home office, drinking coffee and planning next week's content"],
  ["sisällöntuottaja kotitoimistossa juomassa kahvia ja suunnittelemassa ensi viikon sisältöjä", "a content creator in a home office, drinking coffee and planning next week's content"],
  ["kotitoimistossa juo kahvia ja suunnittelee ensi viikon sisältöjä", "in a home office, drinking coffee and planning next week's content"],
  ["istuu kotitoimistossa, juo kahvia ja suunnittelee ensi viikon sisältöjä", "sitting in a home office, drinking coffee and planning next week's content"],
  ["istuu kotitoimistossa juo kahvia ja suunnittelee ensi viikon sisältöjä", "sitting in a home office, drinking coffee and planning next week's content"],
  ["istuu kotitoimistossa ja juo kahvia", "sitting in a home office and drinking coffee"],
  ["henkilö istuu kotitoimistossa läppärin ääressä ja juo kahvia", "a person sitting in a home office at a laptop, drinking coffee"],
  ["henkilö istuu kotitoimistossa läppärin ääressä juomassa kahvia", "a person sitting in a home office at a laptop, drinking coffee"],
  ["henkilö istuu kotitoimistossa läppärillä ja juo kahvia", "a person sitting in a home office at a laptop, drinking coffee"],
  ["henkilö kotitoimistossa läppärin ääressä ja juo kahvia", "a person in a home office at a laptop, drinking coffee"],
  ["henkilö istuu työpöydän ääressä läppäri auki", "the person sitting at a desk with an open laptop"],
  ["henkilö istuu työpöydän ääressä läppäri avoinna", "the person sitting at a desk with an open laptop"],
  ["juo kahvia ja suunnittelee ensi viikon sisältöjä", "drinking coffee and planning next week's content"],
  ["juomassa kahvia ja suunnittelemassa ensi viikon sisältöjä", "drinking coffee and planning next week's content"],
  ["juo kahvia", "drinking coffee"],
  ["juoda kahvia", "drink coffee"],
  ["juomassa kahvia", "drinking coffee"],
  ["suunnittelee ensi viikon sisältöjä", "planning next week's content"],
  ["suunnittelemassa ensi viikon sisältöjä", "planning next week's content"],
  ["ensi viikon sisältöjä", "next week's content"],
  ["tavoite on kasvattaa seuraajia", "the goal is to grow followers"],
  ["tavoitteena on kasvattaa seuraajia", "the goal is to grow followers"],
  ["kasvattaa seuraajia", "grow followers"],
  ["saada lisää sitoutumista", "increase engagement"],
  ["herättää kiinnostus palvelua kohtaan", "spark interest in the service"],
  ["ohjata verkkosivulle", "drive traffic to the website"],
  ["saada liidejä", "generate leads"],
  ["myydä tuotetta tai palvelua", "sell a product or service"],
  ["rakentaa asiantuntijamielikuvaa", "build expert credibility"],
  ["opettaa tai antaa vinkkejä", "teach or share practical tips"],
  ["kohderyhmä on suomalaiset sisällöntuottajat", "the target audience is Finnish content creators"],
  ["kohderyhmä suomalaiset sisällöntuottajat", "target audience: Finnish content creators"],
  ["suomalaisille sisällöntuottajille", "for Finnish content creators"],
  ["suomalaiset yrittäjät", "Finnish entrepreneurs"],
  ["suomalaisille yrittäjille", "for Finnish entrepreneurs"],
  ["suomalaiset yksinyrittäjät", "Finnish solo entrepreneurs"],
  ["suomalaisille yksinyrittäjille", "for Finnish solo entrepreneurs"],
  ["UGC-tekijät", "UGC creators"],
  ["ugc-tekijät", "UGC creators"],
  ["rohkaiseva opettaja ja asiantuntija", "an encouraging teacher and expert"],
  ["rohkaiseva opettaja", "an encouraging teacher"],
  ["opettaja ja asiantuntija", "a teacher and expert"],
  ["innostava", "inspiring"],
  ["yrittäjä kahvilassa", "an entrepreneur in a café"],
  ["kahvilassa", "in a café"],
  ["kahvila", "café"],
  ["nainen istuu metsässä", "a woman sits in a forest"],
  ["nainen istuu kaatuneen puun päällä", "a woman sitting on a fallen tree"],
  ["nainen paistaa makkaraa nuotiolla", "a woman grilling sausages over a campfire"],
  ["nainen istuu kaatuneen puun päällä ja paistaa makkaraa nuotiolla", "a woman sitting on a fallen tree and grilling sausages over a campfire"],
  ["nainen paistaa makkaraa nuotiolla istuen kaatuneen puun päällä", "a woman sitting on a fallen tree and grilling sausages over a campfire"],
  ["nainen istuu", "a woman sits"],
  ["nainen kävelee", "a woman walks"],
  ["nainen katsoo tunturimaisemaa", "a woman looking out over a Finnish fell landscape"],
  ["nainen katsoo järvimaisemaa", "the woman looking at a Finnish lakeside landscape"],
  ["nainen katsoo metsämaisemaa", "the woman looking at a Finnish forest landscape"],
  ["nainen katsoo merimaisemaa", "the woman looking at the sea view"],
  ["nainen katsoo auringonlaskua", "the woman looking at the sunset"],
  ["nainen katsoo maisemaa", "the woman looking at the landscape"],
  ["nainen ihailee tunturimaisemaa", "the woman admiring a Finnish fell landscape"],
  ["nainen ihailee järvimaisemaa", "the woman admiring a Finnish lakeside landscape"],
  ["nainen ihailee auringonlaskua", "the woman admiring the sunset"],
  ["nainen kävelee rannalla auringonlaskussa, katsoo kameraan ja hymyilee kevyesti", "a woman walking on a beach at sunset, looking at the camera and smiling softly"],
  ["nainen kävelee rannalla auringonlaskussa katsoo kameraan ja hymyilee kevyesti", "a woman walking on a beach at sunset, looking at the camera and smiling softly"],
  ["nainen katsoo kameraan ja hymyilee kevyesti", "the woman looking at the camera and smiling softly"],
  ["nainen katsoo kameraan", "a woman looks at the camera"],
  ["henkilö katsoo", "the person looks"],
  ["henkilö kävelee", "the person walks"],
  ["henkilö istuu", "a person sits"],
  ["hahmo istuu", "a person sits"],
  ["henkilö nostaa katseen", "the person raises their gaze"],
  ["henkilö nostaa", "the person lifts"],
  ["henkilö nostaa kahvikupin, hymyilee kevyesti ja katsoo ikkunasta ulos", "the person lifts a coffee cup, smiles softly and looks out of the window"],
  ["henkilö nostaa kahvikupin hymyilee kevyesti ja katsoo ikkunasta ulos", "the person lifts a coffee cup, smiles softly and looks out of the window"],
  ["henkilö nostaa kahvikupin ja hymyilee kameraan", "the person lifts a coffee cup and smiles at the camera"],
  ["nostaa kahvikupin ja hymyilee kameraan", "lifts a coffee cup and smiles at the camera"],
  ["nostaa kahvikupin, hymyilee kevyesti ja katsoo ikkunasta ulos", "lifts a coffee cup, smiles softly and looks out of the window"],
  ["katsoo kameraan", "looks at the camera"],
  ["katsoo ikkunasta ulos", "looks out of the window"],
  ["hymyilee kevyesti", "smiles softly"],
  ["kävelee rannalla", "walks on a beach"],
  ["kävelee rannalla auringonlaskussa", "walking on a beach at sunset"],
  ["rannalla auringonlaskussa", "on a beach at sunset"],
  ["järven rannalla", "by a lakeside"],
  ["laiturilla", "on a wooden dock"],
  ["kotitoimistossa läppärin ääressä", "in a home office at a laptop"],
  ["läppärin ääressä", "at a laptop"],
  ["työpöydän ääressä", "at a desk"],
  ["kaatuneen puun päällä", "on a fallen tree"],
  ["istuen kaatuneen puun päällä", "sitting on a fallen tree"],
  ["paistaa makkaraa", "grilling sausages"],
  ["paistaa makkaraa nuotiolla", "grilling sausages over a campfire"],
  ["grilling sausages over a campfire istuen on a fallen tree", "sitting on a fallen tree and grilling sausages over a campfire"],
  ["nuotiolla", "over a campfire"],
  ["nuotion äärellä", "by a campfire"],
  ["kuvan valo", "the lighting conditions from the reference image"],
  ["muistikirjan ja läppärin kanssa", "with a notebook and laptop"],
  ["seuraavan viikon sisältöjä", "next week's content"],
  ["luonnollinen meikki", "natural makeup"],
  ["rennot hiukset", "relaxed hair"],
  ["neutraali neule", "a neutral sweater"],
  ["henkilön kasvot", "the person's face"],
  ["henkilön kasvot, vaatetus, työpöytä ja huoneen perusasetelma", "the person's face, clothing, desk and basic room setup"],
  ["henkilön kasvot, vaatetus, huoneen asettelu ja läppärin sijainti", "the person's face, clothing, room layout and laptop position"],
  ["huoneen perusasetelma", "the room's basic layout"],
  ["huoneen asettelu", "room layout"],
  ["läppärin sijainti", "the laptop position"],
  ["työpiste", "workspace"],
  ["somevideo", "social media video"],
  ["somevideon", "social media video"],
  ["tekoälystä", "about artificial intelligence"],
  ["somekuva", "social media image"],
  ["AI-kuva", "AI image"],
  ["AI-video", "AI video"],
  ["lyhytvideon käsikirjoitus", "short-form video script"],
  ["UGC-videon käsikirjoitus", "UGC video script"],
  ["somepostaus", "social media post"],
  ["mainosteksti", "ad copy"],
  ["sähköposti", "email"],
  ["myyntisivun tekstiosio", "sales page text section"],
  ["sisältöideat", "content ideas"],
  ["ääniprompti", "audio prompt"],
  ["musiikkiprompti", "music prompt"],
];

const exactFiToEn = new Map(fiToEnPhrases.map(([source, replacement]) => [normalizePhraseKey(source), replacement]));

const fiToEnWords = {
  aito: "authentic",
  aidolta: "authentic",
  aidon: "real",
  aidot: "authentic",
  aidosti: "authentically",
  aamulla: "in the morning",
  aloitus: "opening",
  arkinen: "everyday",
  arjen: "everyday",
  asettelu: "layout",
  auringonlaskussa: "at sunset",
  aihe: "topic",
  aiheesta: "about",
  brändiin: "brand",
  ei: "no",
  elokuvallinen: "cinematic",
  elokuvamainen: "cinematic",
  ensi: "next",
  hyödyntämisestä: "about using",
  hyödyntäminen: "using",
  hyodyntamisesta: "about using",
  hyodyntaminen: "using",
  hidas: "slow",
  hitaasti: "slowly",
  hiukset: "hair",
  helsingin: "Helsinki",
  hahmo: "person",
  hahmon: "person's",
  henkilö: "person",
  henkilöä: "person",
  henkilön: "person's",
  hymy: "smile",
  hymyilee: "smiles",
  hymyilevä: "smiling",
  huone: "room",
  huoneen: "room's",
  hyvä: "good",
  ihminen: "person",
  ihmisen: "person's",
  iho: "skin",
  ihon: "skin",
  illalla: "in the evening",
  ilme: "expression",
  ilmeet: "expressions",
  ja: "and",
  istuu: "sits",
  istuen: "sitting",
  joka: "who",
  juo: "drinking",
  juoda: "drink",
  juomassa: "drinking",
  kanssa: "with",
  kamera: "camera",
  kameraan: "at the camera",
  kameran: "camera",
  kahvikupin: "coffee cup",
  auki: "open",
  avoinna: "open",
  ikkunasta: "out of the window",
  ulos: "out",
  kasvonpiirteet: "facial features",
  kasvojen: "facial",
  kasvoillaan: "on their face",
  kasvot: "face",
  katsoo: "looks",
  kirkko: "church",
  kaupungissa: "in the city",
  kesämaisemassa: "in a summer landscape",
  kevyt: "subtle",
  kevyesti: "softly",
  kiillotettu: "polished",
  kotitoimisto: "home office",
  kotitoimistossa: "in a home office",
  kuva: "image",
  kuvassa: "in the image",
  kuvan: "image",
  kädet: "hands",
  käteen: "into the hand",
  kävelee: "walks",
  kävely: "walking",
  kahvi: "coffee",
  kahvia: "coffee",
  kahvila: "café",
  kahvilassa: "in a café",
  kasvattaa: "grow",
  lämmin: "warm",
  lämpimästi: "warmly",
  läppäri: "laptop",
  läppärillä: "on a laptop",
  läppärin: "laptop",
  laiturilla: "on a wooden dock",
  luonnollinen: "natural",
  luonnollisesti: "naturally",
  luonnolliset: "natural",
  luonto: "nature",
  mainos: "advertisement",
  mainokselta: "advertisement",
  mainosmainen: "promotional",
  makkara: "sausage",
  makkaraa: "sausages",
  metsä: "forest",
  metsässä: "in a forest",
  mies: "man",
  miehestä: "of a man",
  minimalistinen: "minimalistic",
  muistikirja: "notebook",
  muistikirjan: "notebook",
  järven: "lake",
  nainen: "woman",
  naisesta: "of a woman",
  nostaa: "raises",
  nuotiolla: "over a campfire",
  nuotion: "campfire",
  pehmeä: "soft",
  pehmeästi: "softly",
  pieni: "small",
  pöydällä: "on a table",
  opettaja: "teacher",
  rannalla: "on a beach",
  ranta: "beach",
  rauhallinen: "calm",
  realistinen: "realistic",
  realistisesti: "realistically",
  realistisen: "realistic",
  rento: "relaxed",
  rennot: "relaxed",
  rohkaiseva: "encouraging",
  säilytä: "preserve",
  seuraajia: "followers",
  sisällöntuottaja: "content creator",
  sisällöntuottajan: "content creator's",
  sisällöntuottajat: "content creators",
  sisällöntuottajille: "for content creators",
  sisältö: "content",
  sisältöjä: "content pieces",
  sisällöntuotannossa: "in content creation",
  sommittelu: "composition",
  some: "social media",
  somepostaus: "social media post",
  somevideolta: "social media video",
  tausta: "background",
  tekstuuri: "texture",
  tekee: "creates",
  tekoälystä: "about artificial intelligence",
  tekoälyn: "AI",
  tehdä: "to create",
  tekoälymäinen: "AI-looking",
  tilanteessa: "in a situation",
  tavoite: "goal",
  tavoitteena: "the goal is",
  tuote: "product",
  tuotteen: "product",
  tuomiokirkko: "cathedral",
  tuomiokirkkoa: "cathedral",
  tuntuu: "feels",
  tyylikäs: "stylish",
  työpiste: "workspace",
  työpöytä: "desk",
  työpöydän: "desk",
  ulkonäkö: "appearance",
  uskottava: "believable",
  uskottavasti: "believably",
  asiantuntija: "expert",
  valo: "light",
  valoisa: "bright",
  valaistus: "lighting",
  vaatetus: "clothing",
  vaatetuksen: "clothing",
  video: "video",
  videolla: "in the video",
  videon: "video",
  vääristyminen: "distortion",
  ympäristö: "environment",
  ympäristössä: "in an environment",
  yrittäjä: "entrepreneur",
  yrittäjän: "entrepreneur's",
  yrittäjät: "entrepreneurs",
  yrittäjille: "for entrepreneurs",
  yksinyrittäjät: "solo entrepreneurs",
  yksinyrittäjille: "for solo entrepreneurs",
  suomalaiset: "Finnish",
  suomalaisille: "for Finnish",
  suunnittelee: "planning",
  suunnittelemassa: "planning",
  viikon: "week's",
  ylisiloteltu: "over-smoothed",
  haltioitunut: "amazed",
};

const enToFiPhrases = [
  ["no plastic skin", "ei muovista ihoa"],
  ["no overly smooth skin", "ei ylisiloteltua ihoa"],
  ["no beauty filter", "ei kauneusfiltterin tuntua"],
  ["no AI-looking face", "ei tekoälymäisiä kasvoja"],
  ["no distorted hands", "ei vääristyneitä käsiä"],
  ["no warped hands", "ei vääntyneitä käsiä"],
  ["no extra fingers", "ei ylimääräisiä sormia"],
  ["no warped body", "ei vääntynyttä kehoa"],
  ["no unrealistic eyes", "ei epärealistisia silmiä"],
  ["no fake smile", "ei teennäistä hymyä"],
  ["no overprocessed lighting", "ei yliprosessoitua valoa"],
  ["no generic stock photo look", "ei geneeristä kuvapankkikuva-fiilistä"],
  ["no overly perfect composition", "ei liian täydellistä sommittelua"],
  ["no unnatural skin texture", "ei epäluonnollista ihon tekstuuria"],
  ["no unrealistic proportions", "ei epäuskottavia mittasuhteita"],
  ["do not change identity", "älä muuta identiteettiä"],
  ["do not change facial features", "älä muuta kasvonpiirteitä"],
  ["do not change hairstyle", "älä muuta hiustyyliä"],
  ["do not change clothing", "älä muuta vaatetusta"],
  ["no face distortion", "ei kasvojen vääristymistä"],
  ["no extra limbs", "ei ylimääräisiä raajoja"],
  ["no flickering", "ei välkkymistä"],
  ["avoid flicker", "vältä välkkymistä"],
  ["avoid morphing", "vältä muodon vääristymistä"],
  ["avoid extra limbs", "vältä ylimääräisiä raajoja"],
  ["avoid identity changes", "vältä identiteetin muuttumista"],
  ["avoid unrealistic motion", "vältä epärealistista liikettä"],
  ["no unnatural movement", "ei epäluonnollista liikettä"],
  ["no robotic movement", "ei robottimaista liikettä"],
  ["no over-smoothed AI look", "ei ylisiloteltua tekoälymäistä lookia"],
  ["no unrealistic camera movement", "ei epärealistista kameraliikettä"],
  ["no melting objects", "ei sulavia objekteja"],
  ["no changing background", "ei muuttuvaa taustaa"],
  ["no changing product shape", "ei tuotteen muodon muuttumista"],
  ["no exaggerated expressions", "ei ylinäyteltyjä ilmeitä"],
  ["the lighting conditions from the reference image", "kuvan referenssivalo"],
  ["the person's face", "henkilön kasvot"],
  ["AI image", "AI-kuva"],
  ["AI video", "AI-video"],
];

const enToFiWords = {
  authentic: "aito",
  believable: "uskottava",
  camera: "kamera",
  calm: "rauhallinen",
  clothing: "vaatetus",
  face: "kasvot",
  hands: "kädet",
  image: "kuva",
  light: "valo",
  natural: "luonnollinen",
  person: "henkilö",
  product: "tuote",
  realistic: "realistinen",
  video: "video",
  warm: "lämmin",
};

const finnishMarkers = /\b(haluan|tehdä|naisesta|nainen|kävelee|rannalla|rantaa|auringonlaskussa|auringonlaskua|katsoo|kameraan|hymyilee|henkilö|henkilön|kasvot|kasvoillaan|kuvan|valotilanne|metsässä|makkaraa|nuotiolla|läppär|työpöytä|vaatetus|vält|tekoäly|muovinen|vääristyminen|luonnollinen|suomalaisessa|suomalaiset|yrittäjä|yrittäjät|yksinyrittäjät|sisällöntuottaja|sisällöntuottajat|ugc-tekijät|jossa|sumuisella|niityllä|aamuyöstä|hahmo|pyörähtää|huokaa|syvään|tuomiokirkko|tuomiokirkkoa|haltioitunut|tunturimaisemaa|järvimaisemaa|metsämaisemaa|merimaisemaa|maisemaa|juo|juoda|juomassa|kahvia|kahvila|kahvilassa|suunnittelee|suunnittelemassa|ensi|viikon|seuraajia|kasvattaa|tavoite|kohderyhmä|aihe)\b|\b[\p{L}]+(ssa|ssä|lla|llä|sta|stä|ksi|lle|aan|ään|inen|sti)\b|[äö]/iu;

const forbiddenPlaceholderText = /\b(a detailed real-world scene with visible action and setting|a detailed real-world scene|a concrete realistic scene|a clear subject, setting and action|a realistic scene based on the user's idea|based on the user's idea|based on this idea|the user-provided idea|the user-provided detail expressed in natural English|user-provided detail|the user's topic|the user's content goal|the intended audience|the requested length or format|the selected content format|the specific scene described by the user|a natural realistic detail|a concise practical length or format|ready-to-use ready-to-use|the goal is the goal is to grow followers|the goal is the goal is)\b/i;

const polishEnglish = (value) =>
  cleanSpaces(String(value || ""))
    .replace(/\bwoman grilling sausages over a campfire istuen on a fallen tree\b/gi, "a woman sitting on a fallen tree and grilling sausages over a campfire")
    .replace(/\ba woman sits in a Finnish forest on a fallen tree and grilling sausages over a campfire\b/gi, "a woman sitting on a fallen tree in a Finnish forest and roasting sausages over a campfire")
    .replace(/\ba woman sitting in a Finnish forest on a fallen tree and grilling sausages over a campfire\b/gi, "a woman sitting on a fallen tree in a Finnish forest and roasting sausages over a campfire")
    .replace(/\ba woman sitting on a fallen tree and grilling sausages over a campfire\b/gi, "a woman sitting on a fallen tree and roasting sausages over a campfire")
    .replace(/\ba woman grilling sausages over a campfire sitting on a fallen tree\b/gi, "a woman sitting on a fallen tree and grilling sausages over a campfire")
    .replace(/\bgrilling sausages over a campfire sitting on a fallen tree\b/gi, "sitting on a fallen tree and grilling sausages over a campfire")
    .replace(/\ba woman walks on a beach at sunset, looks at the camera and smiles softly\b/gi, "a woman walking on a beach at sunset, looking at the camera and smiling softly")
    .replace(/\ba woman walks on a beach at sunset looks at the camera and smiles softly\b/gi, "a woman walking on a beach at sunset, looking at the camera and smiling softly")
    .replace(/\bwoman looks helsingin tuomiokirkkoa\b/gi, "the woman looks at Helsinki Cathedral")
    .replace(/\bwoman looks tunturimaisemaa\b/gi, "the woman looking out over a Finnish fell landscape")
    .replace(/\bwoman looks järvimaisemaa\b/gi, "the woman looking at a Finnish lakeside landscape")
    .replace(/\bwoman looks metsämaisemaa\b/gi, "the woman looking at a Finnish forest landscape")
    .replace(/\bwoman looks merimaisemaa\b/gi, "the woman looking at the sea view")
    .replace(/\bwoman looks kameraan\b/gi, "the woman looking at the camera")
    .replace(/\bwoman looks auringonlaskua\b/gi, "the woman looking at the sunset")
    .replace(/\bwoman looks Helsinki cathedral\b/gi, "the woman looks at Helsinki Cathedral")
    .replace(/\bperson looks Helsinki cathedral\b/gi, "the person looks at Helsinki Cathedral")
    .replace(/\ba person sits on a wooden dock in a Finnish summer landscape lake on a beach\b/gi, "a person sitting on a wooden dock by a Finnish lakeside in a summer landscape")
    .replace(/\bperson sits on a wooden dock in a Finnish summer landscape lake on a beach\b/gi, "a person sitting on a wooden dock by a Finnish lakeside in a summer landscape")
    .replace(/\bthe person from the attached reference image sits on a wooden dock in a Finnish summer landscape lake on a beach\b/gi, "the person from the attached reference image sitting on a wooden dock by a Finnish lakeside in a summer landscape")
    .replace(/\blooks Helsinki cathedral\b/gi, "looks at Helsinki Cathedral")
    .replace(/\blooks helsingin tuomiokirkkoa\b/gi, "looks at Helsinki Cathedral")
    .replace(/\bwith an amazed, captivated expression on their face\b/gi, "with an amazed, captivated expression on her face")
    .replace(/\bUse image light\b/gi, "Use the lighting conditions from the reference image")
    .replace(/\bimage light\b/gi, "the lighting conditions from the reference image")
    .replace(/\ba realistic scene based on the user's idea\b/gi, "the provided scene")
    .replace(/\bbased on the user's idea\b/gi, "based on the provided scene")
    .replace(/\bthe user-provided idea\b/gi, "the provided idea")
    .replace(/\bthe user-provided detail expressed in natural English\b/gi, "the provided detail")
    .replace(/\buser-provided detail\b/gi, "provided detail")
    .replace(/\ba Finnish a content creator\b/gi, "a Finnish content creator")
    .replace(/\bFinnish content creator in a home office drinking coffee and planning next week's content\b/gi, "Finnish content creator in a home office, drinking coffee and planning next week's content")
    .replace(/\bcontent creator in a home office drinking coffee and planning next week's content\b/gi, "content creator in a home office, drinking coffee and planning next week's content")
    .replace(/\ba person sits in a home office at a laptop and drinking coffee\b/gi, "a person sitting in a home office at a laptop, drinking coffee")
    .replace(/\ba person sitting in a home office at a laptop and drinking coffee\b/gi, "a person sitting in a home office at a laptop, drinking coffee")
    .replace(/\bdrinking coffee and planning next week's content pieces\b/gi, "drinking coffee and planning next week's content")
    .replace(/\bthe goal is grow followers\b/gi, "the goal is to grow followers")
    .replace(/\bthe goal is the goal is to grow followers\b/gi, "the goal is to grow followers")
    .replace(/\bgoal is grow followers\b/gi, "the goal is to grow followers")
    .replace(/\btarget audience is Finnish content creators\b/gi, "the target audience is Finnish content creators")
    .replace(/\bready-to-use ready-to-use\b/gi, "ready-to-use")
    .replace(/\ba a\b/gi, "a")
    .replace(/\ban a\b/gi, "an")
    .replace(/\bhighly realistic realistic\b/gi, "highly realistic")
    .replace(/\brealistic realistic\b/gi, "realistic");

const fallbackEnglish = (value, context = "generic") => {
  const lowered = String(value || "").toLowerCase();
  const original = cleanSpaces(value);
  if (hasMeaningfulUserInput(original) && !forbiddenPlaceholderText.test(original)) return polishEnglish(original);
  if (context === "avoid") return "overly artificial or unrealistic details";
  if (context === "locked") return "identity, facial features, clothing, product appearance and important background details";
  if (context === "lighting") return lowered.includes("kuva") || lowered.includes("valo") ? "the lighting conditions from the reference image" : "realistic natural lighting";
  if (context === "environment") return lowered.includes("mets") ? "a forest environment" : "a realistic real-life environment";
  if (context === "subject") return lowered.includes("nuot") || lowered.includes("makkar") ? "a woman sitting on a fallen tree and grilling sausages over a campfire" : "a clearly specified subject";
  if (context === "action") return lowered.includes("nuot") || lowered.includes("makkar") ? "she gently turns the sausages over the fire" : "a subtle natural movement";
  if (context === "clothing") return "the user-described clothing and styling";
  if (context === "idea" && (lowered.includes("nuot") || lowered.includes("makkar"))) return "a woman sitting on a fallen tree and grilling sausages over a campfire";
  if (context === "idea" && lowered.includes("sumuis")) return "a misty meadow scene before dawn where the character slowly turns around and sighs deeply";
  if (context === "idea") return "a concrete user-provided scene";
  if (context === "contentType") return "content format";
  return "concrete user-provided detail";
};

const safeEnglishOutput = (value, input, context) => {
  const polished = polishEnglish(value);
  if (!polished) return polishEnglish(fallbackEnglish(input, context));
  if (hasMeaningfulUserInput(input) && !forbiddenPlaceholderText.test(polished)) return polished;
  if (!forbiddenPlaceholderText.test(polished) && !hasFinnishLeak(polished)) return polished;
  return polishEnglish(fallbackEnglish(input, context));
};

export const sanitizeEnglishPrompt = (input) =>
  polishEnglish(input)
    .replace(forbiddenPlaceholderText, "concrete filled-field details")
    .replace(/\s+([,.!?;:])/g, "$1")
    .replace(/\s+/g, " ")
    .trim();

export const normalizeToEnglish = (input, context = "generic") => {
  if (Array.isArray(input)) return input.map((item) => normalizeToEnglish(item, context)).filter(Boolean).join(", ");
  detectInputLanguage(input);
  const exact = exactFiToEn.get(normalizePhraseKey(input));
  if (exact) return exact;
  const withPhrases = applyPhraseMap(input, fiToEnPhrases);
  const normalized = polishEnglish(replaceWords(withPhrases, fiToEnWords));
  return safeEnglishOutput(normalized, input, context);
};

export const normalizeUserInputToEnglish = (input, context = "generic") => normalizeToEnglish(input, context);

export const getConcreteSceneDescription = (input, context = "idea") => normalizeUserInputToEnglish(input, context);

export const preprocessUserDescription = (input, context = "generic") => normalizeUserInputToEnglish(input, context);

export const normalizeToFinnish = (input) => {
  if (Array.isArray(input)) return input.map(normalizeToFinnish).filter(Boolean).join(", ");
  const withPhrases = applyPhraseMap(input, enToFiPhrases);
  return cleanSpaces(replaceWords(withPhrases, enToFiWords));
};

export const normalizeFinnishPromptText = (input) =>
  cleanSpaces(String(input || "")
    .replace(/\binstagram mainos\b/gi, "Instagram-mainos")
    .replace(/\bvessin nordic berry\b/gi, "Vessin Nordic Berry")
    .replace(/\bvessin seerumi pulloa\b/gi, "Vessin seerumipulloa")
    .replace(/\bvessin seerumi pullo\b/gi, "Vessin seerumipullo")
    .replace(/\bvessin nordic berry tuotteessa\b/gi, "Vessin Nordic Berry -tuotteesta")
    .replace(/\bvessin nordic berry tuotteesta\b/gi, "Vessin Nordic Berry -tuotteesta")
    .replace(/\bnainen 50 vuotias\b/gi, "noin 50-vuotias nainen")
    .replace(/\b50 vuotias nainen\b/gi, "noin 50-vuotias nainen")
    .replace(/\b50 naiset\b/gi, "noin 50-vuotiaat naiset")
    .replace(/\btilaamaan tuote\b/gi, "saada käyttäjä tilaamaan tuotteen")
    .replace(/\bnordic berry seerumia\b/gi, "Nordic Berry -seerumia")
    .replace(/\bkokeilee vessin\b/gi, "kokeilee Vessin")
    .replace(/\bskandinaavinen koti\b/gi, "skandinaavinen koti")
    .replace(/\bkotiasu\b/gi, "kotiasu")
    .replace(/\bmuistiinpanot\b/gi, "muistiinpanot")
    .replace(/\bläppäri auki\b/gi, "läppäri auki")
    .replace(/\s+([,.!?;:])/g, "$1"));

export const normalizeForLanguage = (value, language = "fi", context = "generic") => {
  const target = String(language || "").toLowerCase();
  return target.includes("eng") || target === "en" ? normalizeToEnglish(value, context) : normalizeToFinnish(value);
};

export const bilingualPrompt = (finnishPrompt, englishPrompt) =>
  `Suomenkielinen prompti:\n${finnishPrompt}\n\nEnglish prompt:\n${englishPrompt}`;

export const hasFinnishLeak = (value) => finnishMarkers.test(String(value || ""));